import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://technical-drawing-analyzer.dev.kuik.tech/api';

// LAKPED-12: rutas cuyo 401 NO significa sesión caducada (Outlook devuelve 401 cuando su
// propia conexión expira, y el login fallido también). Cerrar sesión ahí echaría al usuario.
const PATHS_WITHOUT_SESSION_EXPIRY = ['/auth/login', '/outlook', '/oauth'];
const LOGIN_PATH = '/login';

const api = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

const isSessionExpired = (error) => {
  if (error.response?.status !== 401) return false;
  const url = error.config?.url || '';
  return !PATHS_WITHOUT_SESSION_EXPIRY.some((path) => url.startsWith(path));
};

const redirectToLogin = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('account');
  if (window.location.pathname !== LOGIN_PATH) {
    window.location.href = LOGIN_PATH;
  }
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.metadata = { startedAt: Date.now() };
  return config;
});

const describeAxiosError = (error) => {
  const elapsedMs = error.config?.metadata?.startedAt ? Date.now() - error.config.metadata.startedAt : null;
  const url = error.config ? `${error.config.method?.toUpperCase()} ${error.config.url}` : 'desconocida';

  if (error.response) {
    const status = error.response.status;
    const detailRaw = error.response.data?.detail;
    let detail = '';
    if (typeof detailRaw === 'string') {
      detail = detailRaw;
    } else if (Array.isArray(detailRaw)) {
      // 422 de Pydantic: lista de {type, loc, msg, input, ctx}
      detail = detailRaw
        .map((d) => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d)))
        .join(', ');
    } else if (detailRaw && typeof detailRaw === 'object') {
      detail = detailRaw.msg || JSON.stringify(detailRaw);
    }
    if (!detail) detail = error.response.statusText || '';
    // Reescribir response.data.detail con la versión string para que cualquier
    // consumer que haga setError(err.response.data.detail) reciba string limpio.
    if (error.response.data && detail) {
      error.response.data.detail = detail;
    }
    return {
      userMessage: detail ? `[HTTP ${status}] ${detail}` : `[HTTP ${status}] El servidor devolvió un error`,
      kind: 'server_error',
      status,
      detail,
      elapsedMs,
      url,
    };
  }

  if (error.request) {
    const code = error.code;
    if (code === 'ECONNABORTED' || (error.message && error.message.toLowerCase().includes('timeout'))) {
      return {
        userMessage: `Tiempo de espera agotado tras ${Math.round((elapsedMs || 0) / 1000)}s. Puede que tu red haya cortado la conexión.`,
        kind: 'timeout',
        code,
        elapsedMs,
        url,
      };
    }
    if (code === 'ERR_CANCELED') {
      return { userMessage: 'Petición cancelada', kind: 'canceled', code, elapsedMs, url };
    }
    return {
      userMessage: `No se recibió respuesta del servidor tras ${Math.round((elapsedMs || 0) / 1000)}s. Puede que un firewall o proxy haya cerrado la conexión.`,
      kind: 'no_response',
      code,
      elapsedMs,
      url,
    };
  }

  return { userMessage: error.message || 'Error inesperado al preparar la petición', kind: 'client_error', elapsedMs, url };
};

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const diagnosis = describeAxiosError(error);
    error.diagnosis = diagnosis;
    error.message = diagnosis.userMessage;
    console.error('[API error]', diagnosis, error);

    if (isSessionExpired(error)) {
      console.warn('[API] Sesión caducada, volviendo al login');
      redirectToLogin();
    }

    return Promise.reject(error);
  }
);

export default api;
